import React, { useState, useEffect, useMemo, useRef } from "react";
import { getRiotAssetsContext, getUrl } from "@outplayed/riot-assets";
import CustomCheckbox from "components/CustomCheckbox";
import { ReactComponent as BlueTower } from "svg/blue-tower.svg";
import { ReactComponent as SkinnyCheckmark } from "svg/skinny-checkmark.svg";

const TimelineRow = (props) => {
  const { index, event, allPlayers, proTeam, handleRowMouseStateEnter, handleRowMouseStateLeave, hoveredEvent } = props;

  const { getItemImg, getChampionImgFromSprite } = getRiotAssetsContext();
  const timestamp = event.timestamp;
  const timestampMin = Math.round(parseInt(timestamp) / 1000 / 60);
  const hovered = hoveredEvent === index ? "hovered" : "";

  const side =
    (proTeam === 1 && (event.pid <= 5 || event.killerId <= 5)) ||
    (proTeam === 2 && (event.pid >= 6 || event.killerId >= 6) ? "pro" : "against");
  const eventType =
    event?.eventType === "champion_kill" || event.eventType === "monster_kill"
      ? " killed"
      : event.eventType === "building_kill"
      ? " destroyed"
      : " placed";

  const summoner_killer = event?.summonerName;
  const summoner_victim = event?.victimSummonerName;

  const summonerName1Ref = useRef(null);
  const summonerName2Ref = useRef(null);

  useEffect(() => {
    const summonerName1 = summonerName1Ref.current;
    const summonerName2 = summonerName2Ref.current;

    if (summonerName1?.offsetWidth > 70) {
      summonerName1.textContent = `${summoner_killer.slice(0, 10)}...`;
    }
    if (summonerName2?.offsetWidth > 70) {
      summonerName2.textContent = `${summoner_victim.slice(0, 10)}...`;
    }
  });

  const wardType = event.wardType;
  const wardTypeName = getWardName(wardType);
  const wardImgId = getWardImg(wardType);
  const monsterType = event.monsterType === "dragon" ? event.monsterSubtype : event.monsterType;
  const monsterName = getMonsterName(event.monsterType === "dragon" ? event.monsterSubtype : event.monsterType);
  const monsterImg = getMonsterImg(monsterType);
  const buildingName = getBuildingName(event.buildingType === "tower_building" ? event.towerType : event.buildingType);

  function findChampionId(summonerName) {
    for (const summoner in allPlayers) {
      if (allPlayers[summoner].summonerName === summonerName) {
        return allPlayers[summoner].championId;
      }
    }
  }

  //Default we return the sightward
  function getWardImg(ward) {
    switch (ward) {
      case "yellow_trinket":
        return getItemImg(3340);
      case "blue_trinket":
        return getItemImg(3363);
      case "control_ward":
        return getItemImg(2055);
      case "sight_ward":
        return getItemImg(3340); //????
      default:
        return getItemImg(3340);
    }
  }

  function getWardName(ward) {
    switch (ward) {
      case "yellow_trinket":
        return "Stealth Ward";
      case "blue_trinket":
        return "Farsight Ward";
      case "control_ward":
        return "Control Ward";
      case "sight_ward":
        return "Sight Ward";
      default:
        return "Sight Ward";
    }
  }

  function getMonsterImg(monster) {
    switch (monster) {
      case "baron_nashor":
        return getUrl("lol/icons/baron-nashor.jpg");
      case "air_dragon":
        return getUrl("lol/icons/cloud-drake.jpg");
      case "elder_dragon":
        return getUrl("lol/icons/elder-dragon.jpg");
      case "hextech_dragon":
        return getUrl("lol/icons/hextech-drake.jpg");
      case "fire_dragon":
        return getUrl("lol/icons/infernal-drake.jpg");
      case "earth_dragon":
        return getUrl("lol/icons/mountain-drake.jpg");
      case "water_dragon":
        return getUrl("lol/icons/ocean-drake.jpg");
      case "riftherald":
        return getUrl("lol/icons/rift-herald.jpg");
      case "chemtech_dragon":
        return getUrl("lol/icons/chemtech-dragon.jpg");
      default:
        return "";
    }
  }

  function getMonsterName(monster) {
    switch (monster) {
      case "baron_nashor":
        return "Baron Nashor";
      case "air_dragon":
        return "Cloud Drake";
      case "elder_dragon":
        return "Elder Dragon";
      case "hextech_dragon":
        return "Hextech Drake";
      case "fire_dragon":
        return "Infernal Drake";
      case "earth_dragon":
        return "Mountain Drake";
      case "water_dragon":
        return "Ocean Drake";
      case "riftherald":
        return "Rift Herald";
      case "chemtech_dragon":
        return "Chemtech Drake";
      default:
        return "";
    }
  }

  function getBuildingName(tower) {
    switch (tower) {
      case "outer_turret":
        return "Outer Turret";
      case "inner_turret":
        return "Inner Turret";
      case "base_turret":
        return "Base Turret";
      case "inhibitor_building":
        return "Inhibitor";
      case "nexus_turret":
        return "Nexus Turret";
      default:
        return tower;
    }
  }

  return (
    <div
      className={`timeline-row ${hovered}`}
      onMouseEnter={() => handleRowMouseStateEnter(index)}
      onMouseLeave={() => handleRowMouseStateLeave()}
    >
      <div className="timestamp">{timestampMin} min</div>
      <div className={`event-info ${side}`}>
        <div className="first-half">
          <div className="champion-icon actioner">
            <div className="image-holder"> {getChampionImgFromSprite(findChampionId(summoner_killer), { size: 26 })}</div>
          </div>
          <div className="name-and-action">
            <span ref={summonerName1Ref}>{summoner_killer}</span>
            <span className="action">{eventType}</span>
          </div>
        </div>
        <div className="second-half">
          {event.eventType === "champion_kill" && (
            <>
              <div className="champion-icon victim">
                <div className="image-holder"> {getChampionImgFromSprite(findChampionId(summoner_victim), { size: 26 })} </div>
              </div>
              <span ref={summonerName2Ref}>{`${summoner_victim}`}</span>
            </>
          )}
          {event.eventType === "ward_placed" && (
            <>
              <img className="item-icon" src={wardImgId} alt={wardTypeName} />
              <span>{`${wardTypeName}`} </span>
            </>
          )}
          {event.eventType === "monster_kill" && (
            <>
              <img className="item-icon" src={monsterImg} alt={monsterType} />
              <span>{monsterName} </span>
            </>
          )}
          {event.eventType === "building_kill" && (
            <>
              <div className={`building ${side}`}>
                <BlueTower />
              </div>
              <span>{buildingName}</span>
            </>
          )}
          {event.eventType === "turret_plate_destroyed" && <div>ASDFASDFASDF</div>}
        </div>
      </div>
    </div>
  );
};

const BlankMap = (props) => {
  const { matchEvent, index, hoveredEvent, proTeam, containerWidth } = props;
  const hovered = index === hoveredEvent ? "hovered" : "";
  const side =
    (proTeam === 1 && (matchEvent.pid <= 5 || matchEvent.killerId <= 5)) ||
    (proTeam === 2 && (matchEvent.pid >= 6 || matchEvent.killerId >= 6) ? "pro" : "against");

  //Add the sides to the calculation, right now there's 34px between the border and
  // the side of the map. So 34 x 2 is subtracted AFTER percentage

  //Width between map and border
  //-4 to account for circle width and map border
  const mapWidth = 17 - 4;
  const mapWidthBoth = 2 * mapWidth;
  const size = containerWidth - mapWidthBoth;

  //Summoners rift is (0,0) in the bottom left to (16000,16000)
  let posX;
  let posY;
  if (matchEvent.position) {
    const { x, y } = matchEvent.position[0];
    posX = x / (16000 - mapWidth * 2);
    posY = (y / 16000) * 100;
  }

  return hovered ? (
    <div
      className={`hovered ${side}`}
      style={{ bottom: posY + "%", left: `calc(${size}px * ${posX} + ${mapWidth}px)`, zIndex: "1" }}
    >
      <div className={`dot ${side}`} style={{ zIndex: "1" }} />
    </div>
  ) : (
    <div className={`dot ${side} ${hovered}`} style={{ bottom: posY + "%", left: `calc(${size}px * ${posX} + ${mapWidth}px)` }} />
  );
};

const Timeline = (props) => {
  const { data } = props;
  const allData = data.match.historicalData.timelineData;
  const allPlayers = [...data?.match.matchSummary.teamA, ...data?.match.matchSummary.teamB];
  const mainSummoner = data?.playerInfo?.summonerName;
  const dotsContainerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const [includeKills, setIncludeKills] = useState(true);
  const [includeObjectives, setIncludeObjectives] = useState(true);
  const [includeVisions, setIncludeVisions] = useState(false);
  const [filteredData, setFilteredData] = useState(allData);

  const [hoveredEvent, setHoveredEvent] = useState(null);

  function findSummonerTeam(mainSummoner) {
    const firstHalf = allPlayers.slice(0, 5);
    if (firstHalf.includes(mainSummoner)) {
      return 1;
    }
    return 2;
  }

  function handleRowMouseStateEnter(index) {
    setHoveredEvent(index);
  }

  function handleRowMouseStateLeave() {
    setHoveredEvent(null);
  }

  // Make the three arrays of different data, kills/objectives/vision
  let killData = [];
  let objectiveData = [];
  let visionData = [];

  //Filter data by type
  for (let eventOccurance in allData) {
    if (allData[eventOccurance]["eventType"] === "champion_kill" || allData[eventOccurance]["eventType"] === "monster_kill") {
      killData.push(allData[eventOccurance]);
    } else if (
      allData[eventOccurance]["eventType"] === "building_kill" ||
      allData[eventOccurance]["eventType"] === "turret_plate_destroyed"
    ) {
      objectiveData.push(allData[eventOccurance]);
    } else if (allData[eventOccurance]["eventType"] === "ward_placed") {
      visionData.push(allData[eventOccurance]);
    }
  }

  useEffect(() => {
    const tempData = [];
    if (includeKills) {
      tempData.push(...killData);
    }
    if (includeObjectives) {
      tempData.push(...objectiveData);
    }
    if (includeVisions) {
      tempData.push(...visionData);
    }
    const sortedData = [...tempData].sort((a, b) => a.timestamp - b.timestamp);
    setFilteredData(sortedData);
  }, [includeKills, includeObjectives, includeVisions]);

  useEffect(() => {
    if (dotsContainerRef.current) {
      const containerWidth = dotsContainerRef.current.offsetWidth;
      setContainerWidth(containerWidth);
    }
  }, [dotsContainerRef?.current?.offsetWidth]);

  return (
    <div className="timeline-holder">
      <div className="checkbox-holder">
        <div className="checkboxes">
          <div className="checkbox">
            <CustomCheckbox
              checkboxId="kills"
              isChecked={includeKills}
              onChange={(e) => setIncludeKills(e.target.checked)}
              label={"Kills"}
            />
            {includeKills && <SkinnyCheckmark className="skinny" onClick={() => setIncludeKills(!includeKills)} />}
          </div>
          <div className="checkbox">
            <CustomCheckbox
              checkboxId="objectives"
              isChecked={includeObjectives}
              onChange={(e) => setIncludeObjectives(e.target.checked)}
              label={"Objectives"}
            />
            {includeObjectives && <SkinnyCheckmark className="skinny" onClick={() => setIncludeObjectives(!includeObjectives)} />}
          </div>
          <div className="checkbox">
            <CustomCheckbox
              checkboxId="vision"
              isChecked={includeVisions}
              onChange={(e) => setIncludeVisions(e.target.checked)}
              label={"Vision"}
            />
            {includeVisions && <SkinnyCheckmark className="skinny" onClick={() => setIncludeVisions(!includeVisions)} />}
          </div>
        </div>
      </div>
      <div className="bottom-half">
        <div className="timeline">
          {filteredData.map((matchEvent, index) => {
            return (
              <TimelineRow
                index={index}
                event={matchEvent}
                allPlayers={allPlayers}
                proTeam={findSummonerTeam(mainSummoner)}
                handleRowMouseStateEnter={handleRowMouseStateEnter}
                handleRowMouseStateLeave={handleRowMouseStateLeave}
                hoveredEvent={hoveredEvent}
              />
            );
          })}
        </div>
        <div className="map">
          <div className="right-half">
            <div className="dots-container" ref={dotsContainerRef}>
              {filteredData.map((matchEvent, index) => {
                return (
                  matchEvent?.position && (
                    <BlankMap
                      matchEvent={matchEvent}
                      index={index}
                      hoveredEvent={hoveredEvent}
                      proTeam={findSummonerTeam(mainSummoner)}
                      containerWidth={containerWidth}
                    />
                  )
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Timeline;
